import React from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {Space} from 'antd';
import {StyledJournalEntryTabContainer} from '../../JournalEntriesTabs/StyledJournalEntriesTabs';
import {StyledMissingErpTabIconsSpace} from '../../../NetSuiteBillsPage/tabs/MissingErpTab/StyledMissingErpTab';
import EmptyOrTable from '../../../../EmptyOrTable';
import TableFilters from '../../../NetSuiteVendorsPage/TableFilters';
import {accountingHelpers, formHelpers} from '../../../../../utils/helpers';
import {useJournalEntryTabState} from '../../hooks';

const dateColumnProps = accountingHelpers.getDateColumnProps();

const defaultFilterProps = accountingHelpers.getHistoryTabFilterProps();

const JournalEntryHistoryTab = ({
  data = [],
  empty,
  loading = false,
  onFilter,
  onRowUpdate,
  onRowSelect,
  tableFiltersProps,
  tableProps,
  selectedRowsIds = [],
  ...rest
}) => {
  const [t] = useTranslation('main');
  const {
    customSegments,
    employees,
    expenseCategoryOptions,
    handleOnFilter,
    query,
  } = useJournalEntryTabState(onFilter);

  const getInput = ({ defaultValue, propName, record }) => {
    return accountingHelpers.isEntrySyncStatusError(record?.sync_status)
      ? accountingHelpers.getTableInput({
          defaultValue,
          propName,
          record,
          t,
          onChange: (value) => onRowUpdate?.({ id: record.id, data: { [propName]: value } }),
        })
      : defaultValue;
  };

  const getSelect = ({ defaultValue, levels, options, propName, record }) => {
    return accountingHelpers.isEntrySyncStatusError(record?.sync_status)
      ? accountingHelpers.getTableSelect({
          defaultValue,
          options,
          propName,
          record,
          t,
          onSelect: (value) => onRowUpdate?.({ id: record.id, data: accountingHelpers.getUpdateData({levels, propName, value, entry: record}) }),
          ...rest
        })
      : formHelpers.getOptionValueLabel(options, defaultValue);
  };

  const getVendor = (vendor) => vendor?.name || '';

  const getTransactionDate = (date, record) => {
    return (
      <StyledMissingErpTabIconsSpace size='small' align='center'>
        {accountingHelpers.getEntryTransactionDate(date, record)}
        <Space className='ant-table-cell-actions' size='small' wrap={false}>
          {accountingHelpers.getInvoiceLinkIcon({id: record?.invoice_id, t})}
        </Space>
      </StyledMissingErpTabIconsSpace>
    );
  }

  const columns = [
    { dataIndex: 'remote_id', title: t('jeReference') },
    {
      dataIndex: 'posting_date',
      title: `${t('posting')} ${t('date')}`,
      ...dateColumnProps,
    },
    { dataIndex: 'sync_status', title: t('status'), render: accountingHelpers.getSyncStatus },
    {
      ...dateColumnProps,
      dataIndex: 'transaction_date',
      render: getTransactionDate,
      title: t('date')
    },
    {
      align: 'right',
      dataIndex: 'amount',
      render: (_, record) => accountingHelpers.getEntryAmount(record),
      title: t('amount'),
    },
    {
      dataIndex: 'card_public_token',
      render: accountingHelpers.getEntryCardId,
      title: `${t('card')} ID`
    },
    {
      dataIndex: 'invoice_id',
      render: (_, record) => accountingHelpers.renderJEInvoicePreview(record),
      title: t('invoice')
    },
    {
      dataIndex: 'card_owner_user_id', title: t('Owner'),
      render: (value) => accountingHelpers.getOwnerDetails({ value, employees })
    },
    {
      dataIndex: 'vendor',
      render: getVendor,
      title: t('vendor')
    },
    {
      dataIndex: 'memo',
      render: (defaultValue, record) => getInput({ defaultValue, propName: 'memo', record }),
      title: t('memo'),
    },
    {
      dataIndex: 'debit_account',
      render: (defaultValue, record) => getSelect({ defaultValue, record, options: expenseCategoryOptions, propName: 'debit_account' }),
      title: t('debitAccount'),
    },
    {
      dataIndex: 'credit_account',
      render: (defaultValue, record) => getSelect({ defaultValue, record, options: expenseCategoryOptions, propName: 'credit_account' }),
      title: t('creditAccount'),
    },
    ...accountingHelpers.getCustomSegmentColumns({columns: customSegments, getSelect, linesPropName: 'lines'})
  ];

  return (
    <StyledJournalEntryTabContainer
      {...rest}
      size='large'
      direction='vertical'
    >
      <TableFilters
        {...tableFiltersProps}
        {...defaultFilterProps}
        onFilter={handleOnFilter}
      />
      <EmptyOrTable
        data={data}
        empty={empty}
        loading={loading}
        query={query}
        columns={columns}
        tableProps={tableProps}
      />
    </StyledJournalEntryTabContainer>
  );
}

JournalEntryHistoryTab.propTypes = {
  columns: PropTypes.array,
  data: PropTypes.array,
  empty: PropTypes.any,
  loading: PropTypes.bool,
  onFilter: PropTypes.func,
  onRowUpdate: PropTypes.func,
  selectedRowsIds: PropTypes.array,
  tableFiltersProps: PropTypes.any,
  tableProps: PropTypes.object
}

export default JournalEntryHistoryTab;
